import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

interface Entry {
  rank: number;
  displayName: string;
  score: number;
  dezoomsUsed?: number;
  gamesWon?: number;
  won: boolean;
  isMe: boolean;
}

type Period = 'today' | 'week' | 'month' | 'all-time';

const GLOBAL_PERIOD_API: Record<Period, string> = {
  today: '/leaderboard/today',
  week: '/leaderboard/week',
  month: '/leaderboard/month',
  'all-time': '/leaderboard/all-time',
};

const FRIENDS_PERIOD_API: Record<Period, string> = {
  today: '/leaderboard/friends/today',
  week: '/leaderboard/friends/week',
  month: '/leaderboard/friends/month',
  'all-time': '/leaderboard/friends/all-time',
};

const GLOBAL_PERIOD_PATH: Record<Period, string> = {
  today: '/leaderboard',
  week: '/leaderboard/semaine',
  month: '/leaderboard/mois',
  'all-time': '/leaderboard/tous-les-temps',
};

const FRIENDS_PERIOD_PATH: Record<Period, string> = {
  today: '/leaderboard/amis',
  week: '/leaderboard/amis/semaine',
  month: '/leaderboard/amis/mois',
  'all-time': '/leaderboard/amis/tous-les-temps',
};

function parsePeriod(raw?: string): Period {
  if (raw === 'semaine') return 'week';
  if (raw === 'mois') return 'month';
  if (raw === 'tous-les-temps') return 'all-time';
  return 'today';
}

export function LeaderboardPage() {
  const { period: periodParam } = useParams<{ period?: string }>();
  const { pathname } = useLocation();
  const friendsScope = pathname.startsWith('/leaderboard/amis');
  const period = parsePeriod(periodParam);
  const { user, guest } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [myRank, setMyRank] = useState<number | null>(null);
  const [myScore, setMyScore] = useState<number | null>(null);
  const [myPlayed, setMyPlayed] = useState(false);
  const [myWon, setMyWon] = useState(false);
  const [playersCount, setPlayersCount] = useState(0);
  const [puzzleNumber, setPuzzleNumber] = useState(0);
  const [fetchError, setFetchError] = useState('');

  const periodPath = friendsScope ? FRIENDS_PERIOD_PATH : GLOBAL_PERIOD_PATH;

  useEffect(() => {
    if (friendsScope && (!user || guest)) {
      setFetchError('');
      setEntries([]);
      return;
    }
    setFetchError('');
    const endpoint = friendsScope ? FRIENDS_PERIOD_API[period] : GLOBAL_PERIOD_API[period];
    api<{
      entries: Entry[];
      myRank: number | null;
      myScore: number | null;
      myPlayed: boolean;
      myWon: boolean;
      playersCount: number;
      puzzleNumber?: number;
    }>(endpoint)
      .then((data) => {
        setEntries(data.entries);
        setMyRank(data.myRank);
        setMyScore(data.myScore);
        setMyPlayed(data.myPlayed);
        setMyWon(data.myWon);
        setPlayersCount(data.playersCount);
        setPuzzleNumber(data.puzzleNumber ?? 0);
      })
      .catch(() => {
        setEntries([]);
        if (friendsScope) setFetchError(t('leaderboard.friendsEmpty'));
      });
  }, [period, friendsScope, user, guest]);

  const periodTitle = {
    today: t('leaderboard.periodToday'),
    week: t('leaderboard.periodWeek'),
    month: t('leaderboard.periodMonth'),
    'all-time': t('leaderboard.periodAllTime'),
  }[period];

  if (friendsScope && (!user || guest)) {
    return (
      <main className="page leaderboard">
        <h1>{t('leaderboard.scopeFriends')}</h1>
        <p className="muted">{t('friends.loginPrompt')}</p>
        <Link to="/login" className="btn primary">{t('landing.login')}</Link>
      </main>
    );
  }

  return (
    <main className="page leaderboard">
      <h1>{friendsScope ? t('leaderboard.scopeFriends') : t('nav.leaderboards')}</h1>
      <p className="sub">{periodTitle}</p>

      <nav className="lb-scope-tabs" aria-label={t('leaderboard.scopeNav')}>
        <Link
          to={GLOBAL_PERIOD_PATH[period]}
          className={!friendsScope ? 'lb-period-tab active' : 'lb-period-tab'}
        >
          {t('leaderboard.scopeGlobal')}
        </Link>
        <Link
          to={FRIENDS_PERIOD_PATH[period]}
          className={friendsScope ? 'lb-period-tab active' : 'lb-period-tab'}
        >
          {t('leaderboard.scopeFriends')}
        </Link>
      </nav>

      <nav className="lb-period-tabs" aria-label={t('leaderboard.periodNav')}>
        {(Object.keys(periodPath) as Period[]).map((p) => (
          <Link
            key={p}
            to={periodPath[p]}
            className={p === period ? 'lb-period-tab active' : 'lb-period-tab'}
          >
            {{
              today: t('leaderboard.periodToday'),
              week: t('leaderboard.periodWeek'),
              month: t('leaderboard.periodMonth'),
              'all-time': t('leaderboard.periodAllTime'),
            }[p]}
          </Link>
        ))}
      </nav>

      {period === 'today' && puzzleNumber > 0 && (
        <p className="sub">Énigme #{puzzleNumber}</p>
      )}
      {playersCount > 0 && (
        <p className="lb-players-count">
          {playersCount} {playersCount > 1 ? t('leaderboard.players') : t('leaderboard.player')}
        </p>
      )}
      {user && myPlayed && myWon && myRank != null && (
        <p className="my-rank">{t('leaderboard.myRank')} #{myRank}</p>
      )}
      {user && myPlayed && !myWon && period === 'today' && (
        <p className="my-rank my-rank--lost">
          {t('leaderboard.myPlayed')} — {myScore ?? 0} pt
        </p>
      )}
      {user && myPlayed && myWon && period !== 'today' && myScore != null && (
        <p className="my-rank">{t('leaderboard.myTotal')} {myScore} pt</p>
      )}
      {fetchError && <p className="muted">{fetchError}</p>}
      <ol className="lb-list">
        {entries.map((e) => (
          <li key={`${e.rank}-${e.displayName}`} className={`${e.isMe ? 'me' : ''}${!e.won ? ' lost' : ''}`}>
            <span className="rank">#{e.rank}</span>
            <span className="name">{e.displayName}</span>
            <span className="pts">{e.score}</span>
          </li>
        ))}
      </ol>
      {!entries.length && !fetchError && (
        <p className="muted">{friendsScope ? t('leaderboard.friendsEmpty') : t('leaderboard.empty')}</p>
      )}
      <div className="lb-page-actions">
        <Link to="/play" className="btn primary block">{t('nav.daily')}</Link>
        <Link to="/friends" className="btn secondary block">{t('friends.title')}</Link>
      </div>
    </main>
  );
}
